import type {
  DocumentRef,
  RagChunk,
  RagCollection,
  RagQuery,
  RagService,
  UploadInput,
  UploadOptions,
} from '../../ports/rag'
import type { Page } from '../../types'
import { BackendError } from '../../errors'
import type { Transport } from './transport'

export class HttpRagService implements RagService {
  constructor(private readonly t: Transport) {}

  listCollections(): Promise<RagCollection[]> {
    return this.t.json<RagCollection[]>('GET', '/v1/collections')
  }

  createCollection(name: string): Promise<RagCollection> {
    return this.t.json<RagCollection>('POST', '/v1/collections', { body: { name } })
  }

  async deleteCollection(id: string): Promise<void> {
    await this.t.request('DELETE', `/v1/collections/${id}`)
  }

  upload(input: UploadInput, opts?: UploadOptions): Promise<DocumentRef> {
    const form = new FormData()
    form.set('collectionId', input.collectionId)
    form.set('name', input.name)
    if (input.metadata) form.set('metadata', JSON.stringify(input.metadata))
    form.set('file', toBlob(input.data, input.mimeType), input.name)
    return this.t.json<DocumentRef>('POST', '/v1/documents', { body: form, signal: opts?.signal })
  }

  getDocument(id: string): Promise<DocumentRef> {
    return this.t.json<DocumentRef>('GET', `/v1/documents/${id}`)
  }

  listDocuments(collectionId: string, cursor?: string): Promise<Page<DocumentRef>> {
    return this.t.json<Page<DocumentRef>>('GET', '/v1/documents', {
      query: { collectionId, cursor },
    })
  }

  async deleteDocument(id: string): Promise<void> {
    await this.t.request('DELETE', `/v1/documents/${id}`)
  }

  async waitUntilReady(
    id: string,
    opts?: { signal?: AbortSignal; intervalMs?: number },
  ): Promise<DocumentRef> {
    const interval = opts?.intervalMs ?? 1500
    for (;;) {
      if (opts?.signal?.aborted) throw new BackendError({ code: 'canceled' })
      const doc = await this.getDocument(id)
      if (doc.state === 'ready' || doc.state === 'failed') return doc
      await new Promise((resolve) => setTimeout(resolve, interval))
    }
  }

  search(query: RagQuery, opts?: { signal?: AbortSignal }): Promise<RagChunk[]> {
    return this.t.json<RagChunk[]>('POST', '/v1/search', { body: query, signal: opts?.signal })
  }
}

function toBlob(data: Blob | ArrayBuffer | Uint8Array, type?: string): Blob {
  if (data instanceof Blob) return data
  return new Blob([data as BlobPart], { type: type ?? 'application/octet-stream' })
}
